import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useState, useEffect } from 'react';
import { 
  useGetAllTermsAndConditions, 
  useCreateTermsAndConditions, 
  useUpdateTermsAndConditions,
  useGetTermsAndConditions,
  useActivateTermsAndConditions,
  useDeactivateTermsAndConditions,
  useArchiveTermsAndConditions,
  useDeleteTermsAndConditions,
  useGetTermsAndConditionsByType,
  useGetLatestActiveTermsAndConditionsByType,
  getGetAllTermsAndConditionsQueryKey,
  getGetTermsAndConditionsQueryKey
} from '@/shared/api/client/terms-and-conditions-controller';
import {TermsAndConditionsDTO, TermsAndConditionsType} from "@/shared/api/model";
import {TermsAndConditionsFilter} from "@/modules/termsandconditions/types";

export const useTermsAndConditions = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TermsAndConditionsFilter>({});
  const [selectedType, setSelectedType] = useState<TermsAndConditionsType | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const getErrorMessage = (err: unknown, fallback: string) => {
    if (err instanceof Error && err.message) {
      return err.message;
    }

    if (typeof err === 'object' && err !== null && 'message' in err) {
      const message = (err as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }

    return fallback;
  };

  // GET all terms and conditions
  const { 
    data: allTermsAndConditions, 
    isLoading: isLoadingAll, 
    refetch: refetchAll,
    error: allTermsError 
  } = useGetAllTermsAndConditions();

  // Handle error from the query
  useEffect(() => {
    if (allTermsError) {
      setError(`Error fetching terms and conditions: ${getErrorMessage(allTermsError, 'Unknown error')}`);
      message.error('Failed to fetch terms and conditions');
    }
  }, [allTermsError]);

  // GET terms and conditions by type (only fetched when selectedType is set)
  const {
    data: termsByType,
    error: termsByTypeError
  } = useGetTermsAndConditionsByType(
    selectedType as TermsAndConditionsType,
    { query: { enabled: !!selectedType } }
  );

  // Handle error from terms by type query
  useEffect(() => {
    if (termsByTypeError) {
      setError(`Error fetching terms and conditions by type: ${getErrorMessage(termsByTypeError, 'Unknown error')}`);
      message.error('Failed to fetch terms and conditions by type');
    }
  }, [termsByTypeError]);

  // GET latest active terms and conditions by type (only fetched when selectedType is set)
  const {
    data: latestActiveByType,
    error: latestActiveError
  } = useGetLatestActiveTermsAndConditionsByType(
    selectedType as TermsAndConditionsType,
    { query: { enabled: !!selectedType } }
  );

  // Handle error from latest active terms query
  useEffect(() => {
    if (latestActiveError) {
      setError(`Error fetching latest active terms and conditions: ${getErrorMessage(latestActiveError, 'Unknown error')}`);
      message.error('Failed to fetch latest active terms and conditions');
    }
  }, [latestActiveError]);

  // GET specific terms and conditions (only fetched when selectedId is set)
  const {
    data: termsById,
    error: termsByIdError,
    isLoading: isLoadingById
  } = useGetTermsAndConditions(
    selectedId as string,
    { query: { enabled: !!selectedId } }
  );

  // Handle error from terms by id query
  useEffect(() => {
    if (termsByIdError) {
      setError(`Error fetching terms and conditions: ${getErrorMessage(termsByIdError, 'Unknown error')}`);
      message.error('Failed to fetch terms and conditions');
    }
  }, [termsByIdError]);

  // CREATE new terms and conditions
  const createMutation = useCreateTermsAndConditions({
    mutation: {
      onSuccess: () => {
        message.success('Terms and conditions created successfully');
        invalidateQueries();
      },
      onError: (err) => {
        setError(`Error creating terms and conditions: ${getErrorMessage(err, 'Unknown error')}`);
        message.error('Failed to create terms and conditions');
      }
    }
  });

  // UPDATE terms and conditions
  const updateMutation = useUpdateTermsAndConditions({
    mutation: {
      onSuccess: () => {
        message.success('Terms and conditions updated successfully');
        invalidateQueries();
      },
      onError: (err) => {
        setError(`Error updating terms and conditions: ${getErrorMessage(err, 'Unknown error')}`);
        message.error('Failed to update terms and conditions');
      }
    }
  });

  // ACTIVATE terms and conditions
  const activateMutation = useActivateTermsAndConditions({
    mutation: {
      onSuccess: () => {
        message.success('Terms and conditions activated successfully');
        invalidateQueries();
      },
      onError: (err) => {
        setError(`Error activating terms and conditions: ${getErrorMessage(err, 'Unknown error')}`);
        message.error('Failed to activate terms and conditions');
      }
    }
  });

  // DEACTIVATE terms and conditions
  const deactivateMutation = useDeactivateTermsAndConditions({
    mutation: {
      onSuccess: () => {
        message.success('Terms and conditions deactivated successfully');
        invalidateQueries();
      },
      onError: (err) => {
        setError(`Error deactivating terms and conditions: ${getErrorMessage(err, 'Unknown error')}`);
        message.error('Failed to deactivate terms and conditions');
      }
    }
  });

  // ARCHIVE terms and conditions
  const archiveMutation = useArchiveTermsAndConditions({
    mutation: {
      onSuccess: () => {
        message.success('Terms and conditions archived successfully');
        invalidateQueries();
      },
      onError: (err) => {
        setError(`Error archiving terms and conditions: ${getErrorMessage(err, 'Unknown error')}`);
        message.error('Failed to archive terms and conditions');
      }
    }
  });

  // DELETE terms and conditions
  const deleteMutation = useDeleteTermsAndConditions({
    mutation: {
      onSuccess: () => {
        message.success('Terms and conditions deleted successfully');
        invalidateQueries();
      },
      onError: (err) => {
        setError(`Error deleting terms and conditions: ${getErrorMessage(err, 'Unknown error')}`);
        message.error('Failed to delete terms and conditions');
      }
    }
  });

  // Helper function to invalidate cached queries
  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetAllTermsAndConditionsQueryKey() });
    if (selectedId) {
      queryClient.invalidateQueries({ queryKey: getGetTermsAndConditionsQueryKey(selectedId) });
    }
  };

  // Create a new terms and conditions
  const createTermsAndConditions = async (data: TermsAndConditionsDTO) => {
    try {
      setLoading(true);
      setError(null);
      await createMutation.mutateAsync({ data });
      return true;
    } catch (err) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update existing terms and conditions
  const updateTermsAndConditions = async (data: TermsAndConditionsDTO) => {
    try {
      setLoading(true);
      setError(null);
      await updateMutation.mutateAsync({ data });
      return true;
    } catch (err) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Activate terms and conditions
  const activateTermsAndConditions = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await activateMutation.mutateAsync({ id });
      return true;
    } catch (err) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Deactivate terms and conditions
  const deactivateTermsAndConditions = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await deactivateMutation.mutateAsync({ id });
      return true;
    } catch (err) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Archive terms and conditions
  const archiveTermsAndConditions = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await archiveMutation.mutateAsync({ id });
      return true;
    } catch (err) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete terms and conditions
  const deleteTermsAndConditions = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await deleteMutation.mutateAsync({ id });
      return true;
    } catch (err) {
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Wrapper functions to trigger data fetching
  const getByType = (type: TermsAndConditionsType) => {
    setSelectedType(type);
    return termsByType;
  };

  const getLatestActiveByType = (type: TermsAndConditionsType) => {
    setSelectedType(type);
    return latestActiveByType;
  };

  const getById = (id: string) => {
    setSelectedId(id);
    return null; // Return null initially, component should use termsById from the returned hook values
  };

  return {
    // Data
    termsAndConditions: allTermsAndConditions || [],
    termsById: termsById,
    filter,
    loading: isLoadingAll || loading || isLoadingById,
    error,
    
    // Getters
    getByType,
    getLatestActiveByType,
    getById,
    
    // Actions
    setFilter,
    createTermsAndConditions,
    updateTermsAndConditions,
    activateTermsAndConditions,
    deactivateTermsAndConditions,
    archiveTermsAndConditions,
    deleteTermsAndConditions,
    refetch: refetchAll
  };
};
